import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { AppBreadcrumb, AppContainer } from '@/components/atoms';
import RezeptHistoriesFilterPanel from '@/components/organisms/RezeptHistoriesFilterPanel';
import { useRezeptHistoryCsv } from '@/hooks/admin/useRezeptHistoryCsv';
import { getTenantSiteTypes } from '@/services/tenants';
import type { TenantSiteTypeItem } from '@/services/tenants';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants';
import { normalizeYearmonth } from '@/utils/dateConverter';

const RezeptHistoriesPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const { pathname } = useLocation();
  const { isLoading: isAuthLoading } = useSiteAdminAuth();

  const isSuperAdminRoute = pathname.startsWith('/super-admin/admin');

  // Tenant site types for filter dropdown
  const siteTypesQuery = useQuery({
    queryKey: ['tenants', 'site-types', isSuperAdminRoute ? 'all' : site ?? ''],
    queryFn: () => getTenantSiteTypes(isSuperAdminRoute ? undefined : (site ?? undefined)),
    enabled: !isAuthLoading && !!site,
    staleTime: Infinity,
  });

  const tenantSiteTypeList: TenantSiteTypeItem[] =
    siteTypesQuery.data?.data?.site_types ?? [];

  const canUseSiteTypeFeatures = tenantSiteTypeList.length > 1;

  // Filter state — single site_type, default 'medix'
  const [siteType, setSiteType] = useState<string>('medix');
  const [serviceType, setServiceType] = useState('');
  const [rezeptYearmonthDisplay, setRezeptYearmonthDisplay] = useState('');
  const [clinicNumber, setClinicNumber] = useState('');
  const [forcedOutput, setForcedOutput] = useState(false);
  const [yearmonthError, setYearmonthError] = useState('');

  // Set default siteType when tenant list loads
  useEffect(() => {
    if (tenantSiteTypeList.length > 0) {
      const hasMedix = tenantSiteTypeList.some(t => t.site_type === 'medix');
      if (hasMedix) {
        setSiteType('medix');
      } else {
        setSiteType(tenantSiteTypeList[0].site_type);
      }
    }
  }, [tenantSiteTypeList]);

  // CSV export mutation
  const { mutateAsync: exportCsv, isPending: isExporting } = useRezeptHistoryCsv();
  const [activeExportMode, setActiveExportMode] = useState<'aggregate' | 'divide' | null>(null);

  const handleExport = useCallback(async (exportMode: 'aggregate' | 'divide') => {
    setYearmonthError('');

    const yearmonth = normalizeYearmonth(rezeptYearmonthDisplay) ?? '';

    if (!yearmonth) {
      setYearmonthError('提出年月が入力されていません。');
      return;
    }

    setActiveExportMode(exportMode);
    try {
      await exportCsv({
        rezept_yearmonth: yearmonth,
        service_type: (serviceType as typeof SERVICE_TYPE_A2 | typeof SERVICE_TYPE_R2) || undefined,
        site_type: siteType,
        export_mode: exportMode,
        clinic_number: clinicNumber || undefined,
        forced_output: forcedOutput || undefined,
      });
    } catch {
      // Error already handled by the hook's catch
    } finally {
      setActiveExportMode(null);
    }
  }, [rezeptYearmonthDisplay, serviceType, siteType, clinicNumber, forcedOutput, exportCsv]);

  const handleAggregateExport = useCallback(() => handleExport('aggregate'), [handleExport]);
  const handleDivideExport = useCallback(() => handleExport('divide'), [handleExport]);

  const breadcrumbItems = [{ title: 'レセプトCSV' }];

  return (
    <AppContainer>
      <AppBreadcrumb items={breadcrumbItems} />

      <h3 className="text-xl font-semibold my-4 !font-normal">レセプトCSV</h3>

      <RezeptHistoriesFilterPanel
        canUseSiteTypeFeatures={canUseSiteTypeFeatures}
        tenants={tenantSiteTypeList}
        siteType={siteType}
        onChangeSiteType={setSiteType}
        serviceType={serviceType}
        onChangeServiceType={setServiceType}
        rezeptYearmonth={rezeptYearmonthDisplay}
        onChangeRezeptYearmonth={(v) => { setRezeptYearmonthDisplay(v); setYearmonthError(''); }}
        clinicNumber={clinicNumber}
        onChangeClinicNumber={setClinicNumber}
        forcedOutput={forcedOutput}
        onChangeForcedOutput={setForcedOutput}
        onAggregateExport={handleAggregateExport}
        onDivideExport={handleDivideExport}
        isAggregateExporting={isExporting && activeExportMode === 'aggregate'}
        isDivideExporting={isExporting && activeExportMode === 'divide'}
        yearmonthError={yearmonthError}
      />
    </AppContainer>
  );
};

export default RezeptHistoriesPage;
